import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosAPI from "Includes/api/api";
import { postRequest } from "Includes/api/apicall";
import { GET_URL, POST_URL } from "Includes/urls";
import Swal from "sweetalert2";
const escapeHtml = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Fallback-only (used only if backend PDF fails)
const PREVIEW_IFRAME_STYLES = `
  body { margin: 0; padding: 0; font-family: Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .qWrap { width: 100%; max-width: 794px; margin: 0 auto; padding: 20px 48px; background: #fff; box-sizing: border-box; }
  .qTitle { text-align: center; font-size: 18px; font-weight: 800; margin: 6px 0 14px; }
  .qMeta { font-size: 11pt; line-height: 1.5; margin-top: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 14px; }
  th, td { border: 1px solid #111; padding: 8px 10px; font-size: 11pt; }
  th { background: #f5f5f5; text-align: left; }
  .right { text-align: right; white-space: nowrap; }
  .muted { color: #475467; }
  @media print {
    @page { size: A4; margin: 16mm 16mm 14mm 16mm; }
    .qWrap { padding: 0; }
  }
`;

const QuotationPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ref = React.useRef(null);
  const pdfFrameRef = React.useRef(null);

  const data = location.state?.data || null;

  const [pdfUrl, setPdfUrl] = React.useState("");
  const [pdfError, setPdfError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const handleSaveQuotation = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await postRequest(`${POST_URL.get_url.api}agreements/quotations/`, data);
      Swal.fire({ icon: "success", title: "Saved", text: "Quotation saved successfully" });
      navigate("/quotation/list");
    } catch (e) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to save quotation" });
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    if (!data) return undefined;
    let active = true;
    let url = "";
    setPdfError("");
    setLoading(true);

    axiosAPI
      .post("agreements/quotation/pdf/", data, { responseType: "blob" })
      .then((res) => {
        if (!active) return;
        const blob = new Blob([res.data], { type: "application/pdf" });
        url = URL.createObjectURL(blob);
        setPdfUrl(url);
      })
      .catch(() => {
        if (!active) return;
        setPdfError("Failed to generate PDF from server. Showing fallback preview.");
        setPdfUrl("");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [data]);

  const handlePrint = () => {
    if (pdfUrl && pdfFrameRef.current && pdfFrameRef.current.contentWindow) {
      try {
        pdfFrameRef.current.contentWindow.focus();
        pdfFrameRef.current.contentWindow.print();
        return;
      } catch (e) {
        // ignore and fallback below
      }
    }

    // Fallback: client-side print of the HTML preview
    const node = ref.current;
    if (!node) return;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "print");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    document.body.appendChild(iframe);

    const idoc = iframe.contentDocument;
    if (!idoc) {
      document.body.removeChild(iframe);
      return;
    }

    const clone = node.cloneNode(true);
    idoc.open();
    idoc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${escapeHtml(
        " "
      )}</title><style>${PREVIEW_IFRAME_STYLES}</style></head><body></body></html>`
    );
    idoc.body.appendChild(clone);
    idoc.close();

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(cleanup, 500);
      }
    }, 200);
  };

  React.useEffect(() => {
    if (!location.state?.autoPrint) return undefined;
    if (!pdfUrl) return undefined;
    const t = setTimeout(handlePrint, 200);
    return () => clearTimeout(t);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.autoPrint, pdfUrl]);

  if (!data) {
    return (
      <div style={{ padding: 16 }}>
        <h3>No quotation data found.</h3>
        <button onClick={() => navigate("/quotation/add")}>Go to Quotation</button>
      </div>
    );
  }

  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const lineItems = Array.isArray(data.lineItems) && data.lineItems.length ? data.lineItems : null;
  const subtotal = lineItems
    ? lineItems.reduce((s, row) => s + Number(row.amount || 0), 0)
    : Number(data.amount || 0);
  const gstPct = Number(data.gst || 0);
  const gstAmt = gstPct ? (subtotal * gstPct) / 100 : 0;
  const total = subtotal + gstAmt;

  return (
    <div style={{ background: "#f2f4f7", padding: 16 }}>
      {pdfUrl ? (
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <iframe
              ref={pdfFrameRef}
              title="quotation-pdf"
              src={pdfUrl}
              style={{ width: "100%", height: "86vh", border: 0, display: "block" }}
            />
          </div>
          {loading ? (
            <div style={{ marginTop: 10, color: "#475467" }}>Generating PDF…</div>
          ) : null}
        </div>
      ) : (
        <div ref={ref} className="qWrap">
        <div className="qWrap">
          <div className="qTitle">Quotation</div>
          <div className="qMeta">
            <div><b>Company:</b> {data.company_name || "—"}</div>
            <div className="muted">{data.company_address || ""}</div>
            {(data.contact_person || data.contact_phone) ? (
              <div style={{ marginTop: 6 }}>
                <b>Contact:</b> {data.contact_person || "—"}{data.contact_phone ? ` (${data.contact_phone})` : ""}
              </div>
            ) : null}
            {data.valid_until ? (
              <div style={{ marginTop: 6 }}><b>Valid until:</b> {data.valid_until}</div>
            ) : null}
          </div>

          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Plan</th>
                <th>Service / detail</th>
                <th className="right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {lineItems ? (
                lineItems.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.item || "—"}</td>
                    <td>{row.plan != null && row.plan !== "" ? row.plan : "—"}</td>
                    <td>
                      {row.service != null && row.service !== "" ? row.service : "—"}
                      {row.note ? <div className="muted" style={{ marginTop: 4, fontSize: "10pt" }}>{row.note}</div> : null}
                    </td>
                    <td className="right">{fmt(row.amount)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>Subscription</td>
                  <td>{data.plan_name || "—"}</td>
                  <td>{data.service_name || "—"}</td>
                  <td className="right">{fmt(subtotal)}</td>
                </tr>
              )}
              {lineItems ? (
                <tr>
                  <td colSpan={3} className="right"><b>Subtotal</b></td>
                  <td className="right">₹ {fmt(subtotal)}</td>
                </tr>
              ) : null}
              <tr>
                <td colSpan={3} className="right"><b>GST ({gstPct || 0}%)</b></td>
                <td className="right">₹ {fmt(gstAmt)}</td>
              </tr>
              <tr>
                <td colSpan={3} className="right"><b>Total</b></td>
                <td className="right"><b>₹ {fmt(total)}</b></td>
              </tr>
            </tbody>
          </table>

          {data.notes ? (
            <div style={{ marginTop: 14, fontSize: "11pt" }}>
              <b>Notes:</b>
              <div style={{ whiteSpace: "pre-wrap" }}>{data.notes}</div>
            </div>
          ) : null}
        </div>
      </div>
      )}

      <div style={{ maxWidth: 794, margin: "12px auto 0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={() => navigate("/quotation/add")} style={{ padding: "8px 12px" }}>Back</button>
        <button onClick={handleSaveQuotation} disabled={saving} style={{ padding: "8px 12px", background: "#4680FF", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
          {saving ? "Saving..." : "Save"}
        </button>
        <button onClick={handlePrint} style={{ padding: "8px 12px" }}>
          Print / Save as PDF
        </button>
      </div>
      {pdfError ? (
        <div style={{ maxWidth: 794, margin: "10px auto 0", color: "#b42318" }}>{pdfError}</div>
      ) : null}
    </div>
  );
};

export default QuotationPreview;
